import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  CalendarDaysIcon,
  ClockIcon,
  UserGroupIcon,
  ChartBarIcon,
  FireIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  AdjustmentsVerticalIcon,
  DocumentArrowDownIcon,
  UserCircleIcon,
  BuildingLibraryIcon,
  UsersIcon,
  TableCellsIcon,
  FunnelIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';

const SessionStats = () => {
  const { t } = useLanguage();
  const [timeRange, setTimeRange] = useState('week');
  const [groupBy, setGroupBy] = useState('day');
  const [compareWith, setCompareWith] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  // Fetch session data
  const { data: sessionsData, isLoading } = useQuery(['sessions', timeRange], async () => {
    // Dans un environnement réel, ce serait:
    // const response = await axios.get(`/sessions/stats?timeRange=${timeRange}`);
    // return response.data;
    
    // Données fictives pour la démo
    return generateMockData(timeRange);
  });

  // Générer des données fictives pour la démonstration
  const generateMockData = (range) => {
    const now = new Date();
    const sessions = [];
    const members = {};
    
    // Dates de début et fin basées sur la plage
    let startDate, endDate, intervals;
    endDate = new Date();
    
    switch(range) {
      case 'week':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        intervals = 7;
        break;
      case 'month':
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 1);
        intervals = 30;
        break;
      case 'quarter':
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 3);
        intervals = 90;
        break;
      case 'year':
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 1);
        intervals = 365;
        break;
      default:
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        intervals = 7;
    }
    
    // Générer des données aléatoires pour chaque jour
    for (let i = 0; i < intervals; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      
      // Nombre de séances pour ce jour (plus élevé pour les jours de semaine)
      const dayOfWeek = currentDate.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const sessionCount = isWeekend ? 
        Math.floor(Math.random() * 30) + 10 : // 10-40 pour les week-ends
        Math.floor(Math.random() * 70) + 30;  // 30-100 pour les jours de semaine
      
      // Générer les séances pour ce jour
      for (let j = 0; j < sessionCount; j++) {
        const hourOffset = Math.floor(Math.random() * 14) + 7; // 7h-21h
        const startTime = new Date(currentDate);
        startTime.setHours(hourOffset, Math.floor(Math.random() * 60), 0);
        
        // Durée entre 30 minutes et 2 heures
        const duration = Math.floor(Math.random() * 90) + 30;
        const endTime = new Date(startTime);
        endTime.setMinutes(endTime.getMinutes() + duration);
        
        // ID membre aléatoire entre 1 et 200
        const memberId = Math.floor(Math.random() * 200) + 1;
        if (!members[memberId]) {
          members[memberId] = {
            id: memberId,
            name: `Membre ${memberId}`,
            visits: 0,
            totalDuration: 0,
          };
        }
        members[memberId].visits++;
        members[memberId].totalDuration += duration;
        
        // Type de séance aléatoire
        const types = ['standard', 'class', 'training', 'personal'];
        const type = types[Math.floor(Math.random() * types.length)];
        
        sessions.push({
          id: `session-${sessions.length + 1}`,
          memberId,
          checkInTime: startTime.toISOString(),
          checkOutTime: endTime.toISOString(),
          duration,
          type,
          dayOfWeek,
          hour: hourOffset,
          date: currentDate.toISOString().split('T')[0],
        });
      }
    }
    
    // Données des membres les plus actifs
    const topMembers = Object.values(members)
      .sort((a, b) => b.visits - a.visits)
      .slice(0, 10)
      .map(member => ({
        ...member,
        avgDuration: Math.round(member.totalDuration / member.visits),
      }));
    
    // Calcul des statistiques
    const totalSessions = sessions.length;
    
    // Sessions par jour
    const sessionsByDay = {};
    sessions.forEach(session => {
      const date = session.date;
      sessionsByDay[date] = (sessionsByDay[date] || 0) + 1;
    });
    
    // Sessions par heure
    const sessionsByHour = {};
    for (let h = 7; h <= 21; h++) {
      sessionsByHour[h] = 0;
    }
    sessions.forEach(session => {
      sessionsByHour[session.hour] = (sessionsByHour[session.hour] || 0) + 1;
    });
    
    // Sessions par jour de la semaine
    const sessionsByDayOfWeek = {};
    for (let d = 0; d < 7; d++) {
      sessionsByDayOfWeek[d] = 0;
    }
    sessions.forEach(session => {
      sessionsByDayOfWeek[session.dayOfWeek] = (sessionsByDayOfWeek[session.dayOfWeek] || 0) + 1;
    });
    
    // Durée moyenne
    const avgDuration = Math.round(sessions.reduce((sum, s) => sum + s.duration, 0) / sessions.length);
    
    // Sessions par type
    const sessionsByType = {};
    sessions.forEach(session => {
      sessionsByType[session.type] = (sessionsByType[session.type] || 0) + 1;
    });

    return {
      totalSessions,
      avgDuration,
      sessionsByDay,
      sessionsByHour,
      sessionsByDayOfWeek,
      sessionsByType,
      topMembers,
      sessions,
      timeRange,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  };

  // Calculer les données pour les graphiques
  const getChartData = () => {
    if (!sessionsData) return null;
    
    const labels = [];
    const data = [];
    const compareData = [];
    
    if (groupBy === 'day') {
      // Trier les dates et créer un tableau de données quotidiennes
      const sortedDays = Object.keys(sessionsData.sessionsByDay).sort();
      
      sortedDays.forEach(day => {
        const date = new Date(day);
        labels.push(date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' }));
        data.push(sessionsData.sessionsByDay[day]);
      });
    } else if (groupBy === 'hour') {
      // Données par heure
      for (let h = 7; h <= 21; h++) {
        labels.push(`${h}:00`);
        data.push(sessionsData.sessionsByHour[h] || 0);
      }
    } else if (groupBy === 'weekday') {
      // Données par jour de la semaine
      const weekdays = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
      for (let d = 0; d < 7; d++) {
        labels.push(weekdays[d]);
        data.push(sessionsData.sessionsByDayOfWeek[d] || 0);
      }
    }
    
    return { labels, data, compareData };
  };

  const chartData = getChartData();

  // Formatage pour affichage
  const formatNumber = (num) => {
    return new Intl.NumberFormat('fr-FR').format(num);
  };

  // Calculer les variations par rapport à la période précédente
  const calculateTrends = () => {
    // Dans un vrai scénario, ces valeurs proviendraient d'une comparaison avec la période précédente
    return {
      totalSessionsTrend: Math.random() > 0.5 ? Math.random() * 20 : -Math.random() * 20,
      avgDurationTrend: Math.random() > 0.5 ? Math.random() * 15 : -Math.random() * 15,
      uniqueMembersTrend: Math.random() > 0.5 ? Math.random() * 10 : -Math.random() * 10,
      peakHourTrend: Math.random() > 0.5 ? Math.random() * 25 : -Math.random() * 25,
    };
  };

  const trends = calculateTrends();
  
  // Obtenir l'heure de pointe
  const getPeakHour = () => {
    if (!sessionsData?.sessionsByHour) return { hour: '18:00', count: 0 };
    
    let maxHour = 0;
    let maxCount = 0;
    
    for (const [hour, count] of Object.entries(sessionsData.sessionsByHour)) {
      if (count > maxCount) {
        maxCount = count;
        maxHour = hour;
      }
    }
    
    return { hour: `${maxHour}:00`, count: maxCount };
  };

  const peakHour = getPeakHour();

  // Obtenir le jour de pointe
  const getPeakDay = () => {
    if (!sessionsData?.sessionsByDayOfWeek) return { day: 'Lundi', count: 0 };
    
    const weekdays = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    let maxDay = 0;
    let maxCount = 0;
    
    for (const [day, count] of Object.entries(sessionsData.sessionsByDayOfWeek)) {
      if (count > maxCount) {
        maxCount = count;
        maxDay = parseInt(day);
      }
    }
    
    return { day: weekdays[maxDay], count: maxCount };
  };

  const peakDay = getPeakDay();

  // Exporter les données
  const exportData = () => {
    if (!sessionsData) return;
    
    const jsonString = `data:text/json;chatset=utf-8,${encodeURIComponent(
      JSON.stringify(sessionsData, null, 2)
    )}`;
    
    const link = document.createElement('a');
    link.href = jsonString;
    link.download = `sessions-stats-${timeRange}-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Contrôles et filtres */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div className="flex items-center space-x-3">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">Statistiques des séances</h2>
          <span className="text-slate-400 dark:text-slate-500">|</span>
          <div className="text-slate-600 dark:text-slate-400 text-sm">
            {sessionsData && (
              <>
                {new Date(sessionsData.startDate).toLocaleDateString('fr-FR')} 
                {' '}&mdash;{' '}
                {new Date(sessionsData.endDate).toLocaleDateString('fr-FR')}
              </>
            )}
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {/* Période */}
          <div className="flex bg-slate-50 dark:bg-slate-800/80 rounded-lg overflow-hidden">
            {['week', 'month', 'quarter', 'year'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1.5 text-sm ${
                  timeRange === range 
                    ? 'bg-blue-500 text-slate-900 dark:text-slate-50' 
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/80'
                }`}
              >
                {{
                  week: '7 jours',
                  month: '30 jours',
                  quarter: '3 mois',
                  year: 'Année'
                }[range]}
              </button>
            ))}
          </div>
          
          {/* Grouper par */}
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            className="bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="day">Par jour</option>
            <option value="weekday">Par jour de semaine</option>
            <option value="hour">Par heure</option>
          </select>
          
          {/* Paramètres avancés */}
          <button
            onClick={() => setShowSettingsModal(true)}
            className="bg-slate-50 dark:bg-slate-800/80 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:text-slate-50 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-1.5 flex items-center text-sm transition-colors"
          >
            <AdjustmentsVerticalIcon className="h-4 w-4 mr-1" />
            Options
          </button>
          
          {/* Exporter */}
          <button
            onClick={exportData}
            className="bg-slate-50 dark:bg-slate-800/80 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:text-slate-50 border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-1.5 flex items-center text-sm transition-colors"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
            Exporter
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      ) : (
        <>
          {/* Cartes de statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">Total Séances</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-50">{formatNumber(sessionsData?.totalSessions || 0)}</p>
                  <div className="flex items-center mt-1">
                    {trends.totalSessionsTrend > 0 ? (
                      <div className="flex items-center text-green-400 text-sm">
                        <ArrowUpIcon className="h-3 w-3 mr-1" />
                        <span>+{trends.totalSessionsTrend.toFixed(1)}%</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-red-400 text-sm">
                        <ArrowDownIcon className="h-3 w-3 mr-1" />
                        <span>{trends.totalSessionsTrend.toFixed(1)}%</span>
            </div>
                    )}
            </div>
          </div>
                <div className="bg-blue-500/20 p-3 rounded-xl">
                  <CalendarDaysIcon className="h-6 w-6 text-blue-400" />
          </div>
        </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">Durée Moyenne</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-50">
                    {Math.floor((sessionsData?.avgDuration || 0) / 60)}h {(sessionsData?.avgDuration || 0) % 60}m
                  </p>
                  <div className="flex items-center mt-1">
                    {trends.avgDurationTrend > 0 ? (
                      <div className="flex items-center text-green-400 text-sm">
                        <ArrowUpIcon className="h-3 w-3 mr-1" />
                        <span>+{trends.avgDurationTrend.toFixed(1)}%</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-red-400 text-sm">
                        <ArrowDownIcon className="h-3 w-3 mr-1" />
                        <span>{trends.avgDurationTrend.toFixed(1)}%</span>
            </div>
                    )}
            </div>
          </div>
                <div className="bg-purple-500/20 p-3 rounded-xl">
                  <ClockIcon className="h-6 w-6 text-purple-400" />
          </div>
        </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">Heure de Pointe</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-50">{peakHour.hour}</p>
                  <div className="flex items-center mt-1">
                    {trends.peakHourTrend > 0 ? (
                      <div className="flex items-center text-green-400 text-sm">
                        <ArrowUpIcon className="h-3 w-3 mr-1" />
                        <span>+{trends.peakHourTrend.toFixed(1)}%</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-red-400 text-sm">
                        <ArrowDownIcon className="h-3 w-3 mr-1" />
                        <span>{trends.peakHourTrend.toFixed(1)}%</span>
            </div>
                    )}
            </div>
          </div>
                <div className="bg-orange-500/20 p-3 rounded-xl">
                  <FireIcon className="h-6 w-6 text-orange-400" />
          </div>
        </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">Jour le Plus Fréquenté</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-slate-50">{peakDay.day}</p>
                  <div className="flex items-center mt-1">
                    <span className="text-slate-600 dark:text-slate-400 text-xs">{formatNumber(peakDay.count)} séances</span>
                  </div>
            </div>
                <div className="bg-green-500/20 p-3 rounded-xl">
                  <UserGroupIcon className="h-6 w-6 text-green-400" />
            </div>
          </div>
            </motion.div>
          </div>

          {/* Graphique principal */}
          <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                Fréquentation {groupBy === 'day' ? 'journalière' : groupBy === 'hour' ? 'par heure' : 'par jour de semaine'}
              </h3>
              <div className="text-slate-600 dark:text-slate-400 text-sm">
                {formatNumber(sessionsData?.totalSessions || 0)} séances au total
        </div>
      </div>

            {chartData && (
              <div className="h-72">
                {/* Simulation d'un graphique avec des barres en div */}
                <div className="flex h-full items-end space-x-1">
                  {chartData.data.map((value, index) => {
                    const maxValue = Math.max(...chartData.data);
                    const height = value > 0 ? Math.max(value / maxValue * 100, 3) : 3;
                    
                    return (
                      <div key={index} className="flex-1 flex flex-col items-center group">
                        <div 
                          className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-md hover:from-blue-600 hover:to-blue-500 transition-all relative overflow-visible"
                          style={{ height: `${height}%` }}
                        >
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-blue-500 text-slate-900 dark:text-slate-50 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap">
                            {value} séances
                          </div>
      </div>
                        <div className="text-slate-600 dark:text-slate-400 text-xs mt-2 truncate w-full text-center">
                          {chartData.labels[index]}
            </div>
          </div>
                    );
                  })}
            </div>
          </div>
        )}
          </div>

          {/* Deux colonnes: Top membres et Répartition par type */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top membres */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6"
            >
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-4">
                Top Membres Actifs
              </h3>
              <div className="space-y-4">
                {sessionsData?.topMembers?.slice(0, 5).map((member, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                        {index + 1}
                      </div>
                      <div className="ml-3">
                        <div className="text-slate-900 dark:text-slate-50 font-medium">{member.name}</div>
                        <div className="text-slate-600 dark:text-slate-400 text-xs">{member.visits} séances</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-slate-900 dark:text-slate-50">
                        {Math.floor(member.avgDuration / 60)}h {member.avgDuration % 60}m
                      </div>
                      <div className="text-slate-600 dark:text-slate-400 text-xs">durée moyenne</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-600">
                <button className="w-full text-center text-blue-400 hover:text-blue-300 transition-colors text-sm">
                  Voir tous les membres
                </button>
              </div>
            </motion.div>
            
            {/* Répartition par type */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6"
            >
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-4">
                Répartition par Type
              </h3>
              
              {sessionsData?.sessionsByType && (
                <div className="space-y-4">
                  {Object.entries(sessionsData.sessionsByType).map(([type, count], index) => {
                    const percentage = Math.round((count / sessionsData.totalSessions) * 100);
                    const colors = {
                      standard: 'from-blue-500 to-blue-400',
                      class: 'from-purple-500 to-purple-400',
                      training: 'from-green-500 to-green-400',
                      personal: 'from-orange-500 to-orange-400'
                    };
                    
                    const icons = {
                      standard: UserCircleIcon,
                      class: UserGroupIcon,
                      training: ClockIcon,
                      personal: BuildingLibraryIcon
                    };
                    
                    const Icon = icons[type] || UserCircleIcon;
                    
                    return (
                      <div key={type} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Icon className="h-4 w-4 text-slate-600 dark:text-slate-400 mr-2" />
                            <span className="text-slate-900 dark:text-slate-50">
                              {{
                                standard: 'Standard',
                                class: 'Cours collectif',
                                training: 'Entraînement',
                                personal: 'Coaching personnel'
                              }[type] || type}
                            </span>
                          </div>
                          <div className="text-slate-900 dark:text-slate-50 font-medium">{percentage}%</div>
                        </div>
                        <div className="w-full bg-slate-50 dark:bg-slate-800/80 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full bg-gradient-to-r ${colors[type] || 'from-gray-500 to-gray-400'}`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-600">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-slate-100/80 dark:bg-slate-800/40 rounded-xl">
                    <div className="text-xl font-bold text-slate-900 dark:text-slate-50">{formatNumber(sessionsData?.totalSessions || 0)}</div>
                    <div className="text-slate-600 dark:text-slate-400 text-xs">séances totales</div>
                  </div>
                  <div className="text-center p-3 bg-slate-100/80 dark:bg-slate-800/40 rounded-xl">
                    <div className="text-xl font-bold text-slate-900 dark:text-slate-50">
                      {Object.keys(sessionsData?.sessionsByType || {}).length}
                    </div>
                    <div className="text-slate-600 dark:text-slate-400 text-xs">types de séances</div>
                  </div>
                </div>
            </div>
            </motion.div>
          </div>
          
          {/* Tableau détaillé */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                Données détaillées
              </h3>
              <button className="flex items-center text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 text-sm transition-colors">
                <TableCellsIcon className="h-4 w-4 mr-1" />
                Exporter Excel
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-100/80 dark:bg-slate-800/40">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">Séances</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">Membres Uniques</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">Durée Moyenne</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wider">Heure de Pointe</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {chartData?.labels.slice(0, 5).map((day, index) => (
                    <tr key={index} className="hover:bg-slate-100/80 dark:bg-slate-800/40">
                      <td className="px-4 py-3 whitespace-nowrap text-slate-900 dark:text-slate-50">
                        {day}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-700 dark:text-slate-300">
                        {chartData.data[index]}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-700 dark:text-slate-300">
                        {Math.floor(chartData.data[index] * 0.7)} {/* Estimation */}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-700 dark:text-slate-300">
                        {Math.floor(Math.random() * 60 + 30)}m
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-700 dark:text-slate-300">
                        {17 + Math.floor(Math.random() * 4)}h00
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
      </div>

            <div className="mt-4 text-center">
              <button className="text-blue-400 hover:text-blue-300 transition-colors text-sm">
                Afficher plus
              </button>
            </div>
          </div>
        </>
      )}
      
      {/* Modal des paramètres */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm dark:bg-black/75 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 max-w-lg w-full">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50">Options d'analyse</h2>
              <button 
                onClick={() => setShowSettingsModal(false)}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800/80 rounded-full"
              >
                <XMarkIcon className="h-5 w-5 text-slate-900 dark:text-slate-50" />
              </button>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className="block text-slate-900 dark:text-slate-50 text-sm font-medium mb-2">
                  Période d'analyse
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-slate-600 dark:text-slate-400 text-xs mb-1">
                      Date de début
                    </label>
                    <input 
                      type="date" 
                      className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-600 dark:text-slate-400 text-xs mb-1">
                      Date de fin
                    </label>
                    <input 
                      type="date" 
                      className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-slate-900 dark:text-slate-50 text-sm font-medium mb-2">
                  Comparer avec
                </label>
                <select 
                  className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-600 rounded-lg text-slate-900 dark:text-slate-50"
                  value={compareWith || ''}
                  onChange={(e) => setCompareWith(e.target.value || null)}
                >
                  <option value="">Ne pas comparer</option>
                  <option value="previous">Période précédente</option>
                  <option value="last-year">Même période l'année dernière</option>
                </select>
              </div>
              
              <div>
                <label className="block text-slate-900 dark:text-slate-50 text-sm font-medium mb-2">
                  Filtres supplémentaires
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2 bg-slate-50 dark:bg-slate-800/80 border-slate-200 dark:border-slate-600" />
                    <span className="text-slate-700 dark:text-slate-300">Inclure les séances annulées</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2 bg-slate-50 dark:bg-slate-800/80 border-slate-200 dark:border-slate-600" />
                    <span className="text-slate-700 dark:text-slate-300">Uniquement les membres actifs</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2 bg-slate-50 dark:bg-slate-800/80 border-slate-200 dark:border-slate-600" />
                    <span className="text-slate-700 dark:text-slate-300">Inclure les données de cours collectifs</span>
                  </label>
                </div>
          </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200 dark:border-slate-600">
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className="px-4 py-2 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all"
                >
                  Appliquer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default SessionStats; 